import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const MAIN_DOMAIN = 'quickvee.com';
const SITE_NAME = 'Quickvee';
const SITE_ORIGIN = `https://${MAIN_DOMAIN}`;

const NO_INDEX_PATH_PREFIXES = [
  '/404',
  '/account',
  '/addresses',
  '/buy-it-again',
  '/cart',
  '/changepassword',
  '/checkout',
  '/customer-login',
  '/findstore',
  '/forgot-password',
  '/guest-checkout',
  '/login',
  '/loyalty-points',
  '/merchantlogin',
  '/order',
  '/orders',
  '/ordersummerypage',
  '/payment-cart',
  '/register',
  '/setting',
  '/wishlist',
];

const PUBLIC_EXACT_PATHS = new Set([
  '/',
  '/shop',
  '/about-us',
  '/all-brands',
  '/all-custom-categories',
  '/all-stores',
  '/best-sellers',
  '/blog-about-us',
  '/blog2',
  '/bogo',
  '/brand',
  '/home',
  '/mixnmatch',
  '/new-arrivals',
  '/offers',
  '/popular-products',
  '/privacy-policy',
  '/store',
  '/terms-condition',
  '/how-to-open-a-smoke-shop',
  '/smoke-shop-pos-system',
  '/what-is-a-pos',
]);

const PUBLIC_PATH_PATTERNS = [
  /^\/(?:category|category-product|category-product-near|merchant|product|popularproduct)\/[^/]+$/,
  /^\/old\/merchant\/[^/]+$/,
  /^\/[^/]+\/category\/[^/]+\/[^/]+$/,
];

const PAGE_METADATA = Object.freeze({
  '/': {
    title: 'Quickvee | Local Smoke Shop Pickup & Delivery',
    description:
      'Shop local smoke stores with Quickvee and choose convenient pickup or delivery from nearby merchants.',
  },
  '/shop': {
    title: 'Shop Local Products | Quickvee',
    description:
      'Browse local products and discover nearby Quickvee merchants offering pickup and delivery.',
  },
  '/about-us': {
    title: 'About Quickvee | Local Commerce Made Convenient',
    description:
      'Learn how Quickvee connects shoppers with local merchants for convenient online ordering, pickup, and delivery.',
  },
  '/all-brands': {
    title: 'Browse All Brands | Quickvee',
    description:
      'Explore brands available from local Quickvee merchants and find products for pickup or delivery.',
  },
  '/all-custom-categories': {
    title: 'Browse Departments | Quickvee',
    description:
      'Explore product departments from local Quickvee stores and find what you need faster.',
  },
  '/all-stores': {
    title: 'Find Local Stores | Quickvee',
    description:
      'Discover nearby Quickvee merchants and browse stores offering convenient pickup or delivery.',
  },
  '/best-sellers': {
    title: 'Best-Selling Products | Quickvee',
    description:
      'Browse popular best sellers available from local Quickvee merchants near you.',
  },
  '/blog-about-us': {
    title: 'Quickvee Resources | Retail and Shopping Insights',
    description:
      'Read Quickvee resources about local retail, point-of-sale technology, and smarter shopping experiences.',
  },
  '/blog2': {
    title: 'Quickvee Blog | Local Retail Insights',
    description:
      'Explore practical Quickvee articles about smoke shops, retail technology, and local commerce.',
  },
  '/bogo': {
    title: 'Buy One, Get One Deals | Quickvee',
    description:
      'Discover buy-one-get-one offers available from participating local Quickvee merchants.',
  },
  '/home': {
    title: 'Shop Nearby Stores | Quickvee',
    description:
      'Browse nearby stores, products, brands, and local offers with pickup and delivery through Quickvee.',
  },
  '/mixnmatch': {
    title: 'Mix & Match Deals | Quickvee',
    description:
      'Build a qualifying mix-and-match purchase from participating local Quickvee merchants.',
  },
  '/new-arrivals': {
    title: 'New Arrivals | Quickvee',
    description:
      'Explore newly available products from local Quickvee merchants near you.',
  },
  '/offers': {
    title: 'Nearby Offers and Deals | Quickvee',
    description:
      'Find current offers from nearby Quickvee merchants and shop eligible products locally.',
  },
  '/popular-products': {
    title: 'Popular Products | Quickvee',
    description:
      'Discover products shoppers are browsing from local Quickvee stores near you.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Quickvee',
    description:
      'Review how Quickvee collects, uses, and protects information when you use its services.',
  },
  '/store': {
    title: 'Local Store | Quickvee',
    description:
      'Browse a local Quickvee merchant and shop available products for pickup or delivery.',
  },
  '/terms-condition': {
    title: 'Terms and Conditions | Quickvee',
    description:
      'Read the terms and conditions that apply when using Quickvee services.',
  },
});

const BLOG_METADATA = Object.freeze({
  '/how-to-open-a-smoke-shop': {
    title: 'How to Open a Smoke Shop | Quickvee',
    description:
      'Learn the key planning, compliance, inventory, and retail steps involved in opening a smoke shop.',
  },
  '/smoke-shop-pos-system': {
    title: 'Smoke Shop POS System Guide | Quickvee',
    description:
      'Learn what to consider when choosing point-of-sale technology for a modern smoke shop.',
  },
  '/what-is-a-pos': {
    title: 'What Is a POS System? | Quickvee',
    description:
      'Understand point-of-sale systems and how they support payments, inventory, and retail operations.',
  },
});

const DEFAULT_METADATA = Object.freeze({
  title: 'Quickvee | Shop Local Stores',
  description:
    'Discover products from local Quickvee merchants with convenient pickup and delivery options.',
});

const NOT_FOUND_METADATA = Object.freeze({
  title: 'Page Not Found | Quickvee',
  description: 'The requested Quickvee page could not be found.',
});

const normalizePathname = (pathname) => {
  if (typeof pathname !== 'string' || pathname.length === 0) return '/';
  const withLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return withLeadingSlash === '/'
    ? withLeadingSlash
    : withLeadingSlash.replace(/\/+$/, '');
};

const normalizeMetadataText = (value, maxLength = 80) => {
  if (typeof value !== 'string') return '';
  return value
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength)
    .trim();
};

const humanizePathSegment = (value) => {
  if (!value) return '';

  let decodedValue = value;
  try {
    decodedValue = decodeURIComponent(value);
  } catch {
    // Retain the original segment when malformed URL encoding is received.
  }

  const normalizedValue = normalizeMetadataText(
    decodedValue.replace(/[-_]+/g, ' '),
  );
  return normalizedValue.replace(/\b\w/g, (character) =>
    character.toUpperCase(),
  );
};

const getBrandName = (search) => {
  const searchParams = new URLSearchParams(search || '');
  return normalizeMetadataText(searchParams.get('brandname') || '');
};

const isPathOrChild = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const isKnownPublicPath = (pathname) =>
  PUBLIC_EXACT_PATHS.has(pathname.toLowerCase()) ||
  PUBLIC_PATH_PATTERNS.some((pattern) => pattern.test(pathname.toLowerCase()));

const getPrivatePageMetadata = (pathname) => {
  const normalizedPathname = pathname.toLowerCase();

  if (isPathOrChild(normalizedPathname, '/payment-cart')) {
    return {
      title: 'Secure Checkout | Quickvee',
      description: 'Review and complete your Quickvee order securely.',
    };
  }
  if (isPathOrChild(normalizedPathname, '/ordersummerypage')) {
    return {
      title: 'Order Confirmation | Quickvee',
      description: 'Review the status and details of your Quickvee order.',
    };
  }
  if (isPathOrChild(normalizedPathname, '/order')) {
    return {
      title: 'Your Account | Quickvee',
      description: 'Manage your Quickvee profile, orders, and preferences.',
    };
  }
  if (isPathOrChild(normalizedPathname, '/wishlist')) {
    return {
      title: 'Your Wishlist | Quickvee',
      description: 'Review products saved to your Quickvee wishlist.',
    };
  }
  if (isPathOrChild(normalizedPathname, '/buy-it-again')) {
    return {
      title: 'Buy It Again | Quickvee',
      description: 'Quickly revisit products from your previous Quickvee orders.',
    };
  }
  if (isPathOrChild(normalizedPathname, '/customer-login')) {
    return {
      title: 'Customer Sign In | Quickvee',
      description: 'Sign in to your Quickvee customer account.',
    };
  }
  if (isPathOrChild(normalizedPathname, '/merchantlogin')) {
    return {
      title: 'Merchant Sign In | Quickvee',
      description: 'Sign in to your Quickvee merchant account.',
    };
  }
  if (isPathOrChild(normalizedPathname, '/register')) {
    return {
      title: 'Create an Account | Quickvee',
      description: 'Create a Quickvee customer account.',
    };
  }
  if (isPathOrChild(normalizedPathname, '/forgot-password')) {
    return {
      title: 'Reset Your Password | Quickvee',
      description: 'Request a secure password reset for your Quickvee account.',
    };
  }
  if (isPathOrChild(normalizedPathname, '/changepassword')) {
    return {
      title: 'Choose a New Password | Quickvee',
      description: 'Securely choose a new password for your Quickvee account.',
    };
  }
  if (isPathOrChild(normalizedPathname, '/guest-checkout')) {
    return {
      title: 'Guest Checkout | Quickvee',
      description: 'Continue your Quickvee order without creating an account.',
    };
  }
  if (
    NO_INDEX_PATH_PREFIXES.some((prefix) =>
      isPathOrChild(normalizedPathname, prefix),
    )
  ) {
    return {
      title: 'Account Access | Quickvee',
      description: 'Access a private Quickvee account page.',
    };
  }

  return null;
};

const getPageMetadata = (pathname, brandName) => {
  const normalizedPathname = pathname.toLowerCase();

  if (normalizedPathname === '/404') {
    return NOT_FOUND_METADATA;
  }

  if (normalizedPathname === '/brand') {
    return brandName
      ? {
          title: `${brandName} Products | Quickvee`,
          description: `Browse ${brandName} products available from local Quickvee merchants.`,
        }
      : {
          title: 'Brand Not Selected | Quickvee',
          description: 'Choose a brand to browse products on Quickvee.',
        };
  }

  if (PAGE_METADATA[normalizedPathname]) {
    return PAGE_METADATA[normalizedPathname];
  }
  if (BLOG_METADATA[normalizedPathname]) {
    return BLOG_METADATA[normalizedPathname];
  }

  const segments = pathname.split('/').filter(Boolean);
  if (segments[1]?.toLowerCase() === 'category' && segments.length === 4) {
    const categoryName = humanizePathSegment(segments[2]);
    return {
      title: `${categoryName || 'Category'} Products | Quickvee`,
      description: `Browse ${categoryName || 'category'} products from this local Quickvee merchant.`,
    };
  }

  if (
    ['category', 'category-product', 'category-product-near'].includes(
      segments[0]?.toLowerCase(),
    )
  ) {
    const categoryName = humanizePathSegment(segments[1]);
    return {
      title: `${categoryName || 'Category'} Near You | Quickvee`,
      description: `Browse ${categoryName || 'category'} products available from local Quickvee stores.`,
    };
  }

  if (segments[0]?.toLowerCase() === 'product') {
    return {
      title: 'Product Details | Quickvee',
      description:
        'Review product details, available options, and fulfillment choices from a local Quickvee merchant.',
      type: 'product',
    };
  }

  if (
    segments[0]?.toLowerCase() === 'merchant' ||
    normalizedPathname.startsWith('/old/merchant/')
  ) {
    return {
      title: 'Shop a Local Store | Quickvee',
      description:
        'Browse products from a local Quickvee merchant and choose pickup or delivery where available.',
    };
  }

  if (isKnownPublicPath(pathname)) return DEFAULT_METADATA;
  return getPrivatePageMetadata(pathname) || NOT_FOUND_METADATA;
};

const buildCanonicalUrl = (pathname, brandName) => {
  const normalizedPathname = pathname.toLowerCase();
  if (normalizedPathname === '/brand' && brandName) {
    return `${SITE_ORIGIN}/brand?brandname=${encodeURIComponent(brandName)}`;
  }
  const canonicalPathname = PUBLIC_EXACT_PATHS.has(normalizedPathname)
    ? normalizedPathname
    : pathname;
  return `${SITE_ORIGIN}${canonicalPathname}`;
};

const SeoControl = () => {
  const location = useLocation();
  const currentPath = normalizePathname(location.pathname);
  const normalizedPathForComparison = currentPath.toLowerCase();
  const brandName = getBrandName(location.search);
  const metadata = getPageMetadata(currentPath, brandName);
  const host =
    typeof window === 'undefined'
      ? MAIN_DOMAIN
      : window.location.hostname.toLowerCase();

  const isAllowedDomain =
    host === MAIN_DOMAIN ||
    host === `www.${MAIN_DOMAIN}` ||
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '[::1]';
  const isExplicitlyPrivatePath = NO_INDEX_PATH_PREFIXES.some((prefix) =>
    isPathOrChild(normalizedPathForComparison, prefix),
  );
  const isInvalidBrandPath =
    normalizedPathForComparison === '/brand' && !brandName;
  const isUnknownPath = !isKnownPublicPath(currentPath);
  const shouldNoIndexPath =
    isExplicitlyPrivatePath || isInvalidBrandPath || isUnknownPath;
  const shouldNoIndex = !isAllowedDomain || shouldNoIndexPath;
  const canonicalUrl = buildCanonicalUrl(currentPath, brandName);
  const shouldRenderCanonical = !shouldNoIndexPath;
  const robotsContent = shouldNoIndex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large';

  return (
    <Helmet>
      <title>{metadata.title}</title>
      <meta name="description" content={metadata.description} />
      <meta name="robots" content={robotsContent} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content={metadata.type || 'website'} />
      <meta property="og:title" content={metadata.title} />
      <meta property="og:description" content={metadata.description} />
      {shouldRenderCanonical && (
        <meta property="og:url" content={canonicalUrl} />
      )}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={metadata.title} />
      <meta name="twitter:description" content={metadata.description} />
      {shouldRenderCanonical && <link rel="canonical" href={canonicalUrl} />}
    </Helmet>
  );
};

export default SeoControl;
